import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { CardModule } from 'primeng/card';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { Subject, finalize, forkJoin, catchError, of, debounceTime, switchMap, map } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CrmAccount, CrmClient } from '../../crm/crm.model';
import { CrmService } from '../../crm/crm.service';
import { Driver } from '../../fleet/driver/driver.model';
import { QuickFleetOnboardingDialogComponent, QuickFleetOnboardingResult } from '../../fleet/quick-fleet-onboarding/quick-fleet-onboarding-dialog.component';
import { SessionStorageService } from '../../services/session-storage.service';
import { FirebaseStorageService } from '../../services/firebase-storage.service';
import { PageFormSectionComponent } from '../../shared/page-layout/page-form-section.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { companyOwnedWarehouses, masterDataMap, warehousingBreadcrumb, warehousingHomeBreadcrumb } from '../warehousing-common';
import { WarehousingBulkReceiveCommand, WarehousingInboundReceipt, WarehousingItem, WarehousingMasterData, WarehousingStockEntry, WarehousingShipmentInboundRequest, WarehousingShipmentInboundReceiveLine } from '../warehousing.model';
import { WarehousingService } from '../warehousing.service';
import { itemIdentityLabel, itemSizeLabel as formatItemSize } from '../warehousing-item-display';

@Component({
  selector: 'app-warehousing-bulk-receiving-form',
  standalone: true,
  imports: [
    CommonModule, FormsModule, ReactiveFormsModule, RouterModule, BreadcrumbModule, ButtonModule, CardModule, CheckboxModule,
    DatePickerModule, DialogModule, IftaLabelModule, InputGroupModule, InputGroupAddonModule,
    InputNumberModule, InputTextModule, ProgressBarModule, SelectModule, TableModule, TextareaModule,
    ToastModule, PageFormSectionComponent, PageHeroComponent, PageShellComponent,
    QuickFleetOnboardingDialogComponent
  ],
  providers: [MessageService],
  templateUrl: './warehousing-bulk-receiving-form.component.html',
  styles: [`
    .bulk-receiving-form {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      min-width: 0;
    }

    .manifest-toolbar,
    .manifest-actions {
      gap: 0.75rem;
    }

    .manifest-table-scroll {
      padding-bottom: 0.25rem;
    }

    .manifest-table-scroll td {
      vertical-align: top;
    }

    @media (max-width: 640px) {
      .manifest-toolbar > p-button,
      .manifest-actions > p-button {
        width: 100%;
      }
    }
  `]
})
export class WarehousingBulkReceivingFormComponent implements OnInit {
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private warehousingService = inject(WarehousingService);
  private crmService = inject(CrmService);
  private destroyRef = inject(DestroyRef);
  private firebaseStorageService = inject(FirebaseStorageService);
  private sessionStorageService = inject(SessionStorageService);
  private messageService = inject(MessageService);

  isLoading = false;
  isSaving = false;
  quickFleetOnboardingVisible = false;
  quickSupplierVisible = false;
  isCreatingSupplier = false;
  successDialogVisible = false;
  createdReceipt: WarehousingInboundReceipt | null = null;
  inboundRequest?: WarehousingShipmentInboundRequest;
  reviewedManualReceipts = false;
  dnFiles: File[] = [];

  breadcrumbItems: MenuItem[] = warehousingBreadcrumb('Receive Stock', 'Bulk Receiving');
  homeBreadcrumb: MenuItem = warehousingHomeBreadcrumb();

  items: Array<WarehousingItem & { displayLabel: string }> = [];
  clients: CrmClient[] = [];
  accounts: CrmAccount[] = [];
  drivers: Driver[] = [];
  driversLoading = false;
  driverLoadError = false;
  private driverPage = -1;
  private driverHasMore = false;
  private driverSearch = '';
  private driverSearch$ = new Subject<{ search: string; page: number }>();
  showMoreDetails = false;
  masterData = masterDataMap();

  bulkForm = this.fb.group({
    referenceNumber: [''],
    supplierId: [null as number | null],
    driverId: [null as number | null],
    vehiclePlateNumber: [''],
    warehouseId: [null as number | null, [Validators.required]],
    receiptDate: [new Date(), [Validators.required]],
    notes: [''],
    lines: this.fb.array([])
  });
  quickSupplierForm = this.fb.group({
    companyName: ['', Validators.required],
    primaryEmail: ['', Validators.email],
    phoneNumber: ['']
  });

  get linesArray(): FormArray {
    return this.bulkForm.get('lines') as FormArray;
  }

  ngOnInit(): void {
    this.driverSearch$.pipe(
      debounceTime(250),
      switchMap(({ search, page }) => {
        this.driversLoading = true;
        this.driverLoadError = false;
        return this.warehousingService.searchReceivingDrivers(search, page, this.bulkForm.controls.driverId.value).pipe(
          map(result => ({ result, page, search })),
          catchError(() => {
            this.driverLoadError = true;
            return of({ result: { items: [] as Driver[], hasMore: false }, page, search });
          }),
          finalize(() => this.driversLoading = false)
        );
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(({ result, page, search }) => {
      if (search !== this.driverSearch) return;
      const selected = this.drivers.find(driver => driver.id === this.bulkForm.controls.driverId.value);
      const existing = page === 0 ? (selected ? [selected] : []) : this.drivers;
      this.drivers = [...existing, ...(result.items || []).filter(driver => !existing.some(value => value.id === driver.id))];
      this.driverPage = page;
      this.driverHasMore = !!result.hasMore;
    });
    this.loadReferenceData();
    this.addLine(); // start with one blank line
    this.bulkForm.controls.warehouseId.valueChanges.subscribe(warehouseId => {
      const zones = this.masterData.ZONE.filter(zone => zone.parentId === warehouseId);
      this.linesArray.controls.forEach(line => line.get('zoneId')?.setValue(zones.length === 1 ? zones[0].id : null));
    });
  }

  loadReferenceData(): void {
    this.isLoading = true;
    const companyId = this.sessionStorageService.getCompany()?.id || 0;
    const inboundId = Number(this.route.snapshot.queryParamMap.get('shipmentInboundRequestId'));
    forkJoin({
      items: this.warehousingService.getItems(),
      clients: this.crmService.getClients(companyId, inboundId > 0 ? undefined : ['SUPPLIER', 'WAREHOUSE_SUPPLIER']),
      accounts: this.crmService.getAccounts(),
      regions: this.warehousingService.getMasterData('REGION'),
      warehouses: this.warehousingService.getMasterData('WAREHOUSE'),
      zones: this.warehousingService.getMasterData('ZONE'),
      conditions: this.warehousingService.getMasterData('ITEM_CONDITION')
    }).pipe(
      finalize(() => this.isLoading = false)
    ).subscribe({
      next: res => {
        this.items = (res.items.data || []).filter(item => item.isActive !== false)
          .map(item => ({ ...item, displayLabel: itemIdentityLabel(item) }));
        this.clients = res.clients || [];
        this.accounts = res.accounts || [];
        this.masterData.REGION = res.regions.data || [];
        this.masterData.WAREHOUSE = companyOwnedWarehouses(res.warehouses.data || []);
        this.masterData.ZONE = res.zones.data || [];
        this.masterData.ITEM_CONDITION = res.conditions.data || [];

        if (this.masterData.WAREHOUSE.length === 1 && !this.bulkForm.value.warehouseId) {
          this.bulkForm.patchValue({ warehouseId: this.masterData.WAREHOUSE[0].id });
        }
        const defaultCondition = this.masterData.ITEM_CONDITION.find(c => c.name?.toLowerCase().includes('good'))?.id
          || this.masterData.ITEM_CONDITION[0]?.id || null;
        this.linesArray.controls.forEach(line => {
          if (!line.get('conditionId')?.value) line.get('conditionId')?.setValue(defaultCondition);
        });
        if (inboundId > 0) this.loadLinkedInbound(inboundId);
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Load Error',
          detail: 'Could not load master data for bulk receiving.'
        });
      }
    });
  }

  createLineGroup(): FormGroup {
    const defaultCondition = this.masterData.ITEM_CONDITION.find(c => c.name?.toLowerCase().includes('good'))?.id
      || (this.masterData.ITEM_CONDITION[0]?.id || null);

    return this.fb.group({
      itemId: [null as number | null, [Validators.required]],
      requestLineId: [null as number | null],
      expectedQuantity: [null as number | null],
      disposition: ['AVAILABLE' as 'AVAILABLE' | 'HOLD'],
      exceptionReason: [''],
      zoneId: [null as number | null, [Validators.required]],
      conditionId: [defaultCondition, [Validators.required]],
      quantity: [1, [Validators.required, Validators.min(0.0001)]],
      transactionUomId: [null as number | null],
      unitSkusText: [''],
      packageQuantity: [null as number | null],
      remarks: ['']
    });
  }

  private loadLinkedInbound(id: number): void {
    this.isLoading = true;
    this.warehousingService.getShipmentInbound(id).pipe(finalize(() => this.isLoading = false)).subscribe({
      next: response => {
        const request = response.data;
        if (!request) return;
        this.inboundRequest = request;
        this.bulkForm.patchValue({ warehouseId: request.warehouseId, referenceNumber: request.shipmentNumber });
        this.bulkForm.controls.warehouseId.disable();
        this.bulkForm.controls.referenceNumber.disable();
        this.bulkForm.controls.receiptDate.disable();
        this.linesArray.clear();
        for (const expected of request.lines.filter(line => line.status === 'READY')) {
          const group = this.createLineGroup();
          group.patchValue({
            requestLineId: expected.id, itemId: expected.itemId, expectedQuantity: expected.expected,
            quantity: expected.expected,
            zoneId: this.availableZones().length === 1 ? this.availableZones()[0].id : null
          });
          group.get('quantity')?.setValidators([Validators.required, Validators.min(0), Validators.max(expected.expected)]);
          group.get('quantity')?.updateValueAndValidity();
          group.get('zoneId')?.clearValidators();
          group.get('conditionId')?.clearValidators();
          group.get('itemId')?.disable();
          group.get('transactionUomId')?.disable();
          this.linesArray.push(group);
        }
        if (!this.linesArray.length) this.messageService.add({ severity: 'info', summary: 'Awaiting delivery', detail: 'Complete the destination stop before receiving this cargo.' });
      },
      error: error => this.messageService.add({ severity: 'error', summary: 'Inbound request', detail: error?.error?.message || 'Could not load inbound work.' })
    });
  }

  private submitLinkedInbound(): void {
    const request = this.inboundRequest;
    if (!request || !this.linesArray.length) return;
    if (this.bulkForm.invalid) {
      this.bulkForm.markAllAsTouched();
      this.messageService.add({ severity: 'warn', summary: 'Validation required', detail: 'Complete the physical count, zone and condition for each line.' });
      return;
    }
    if (request.possibleManualReceipts.length && !this.reviewedManualReceipts) {
      this.messageService.add({ severity: 'warn', summary: 'Review possible receipts', detail: 'Review matching manual receipts before issuing another GRN.' });
      return;
    }
    const lines: WarehousingShipmentInboundReceiveLine[] = this.linesArray.getRawValue().map((line: any) => ({
      lineId: line.requestLineId, quantity: Number(line.quantity), zoneId: line.zoneId,
      conditionId: line.conditionId, disposition: line.disposition,
      unitCodes: this.unitSkus(line.unitSkusText), exceptionReason: line.exceptionReason?.trim() || undefined
    }));
    for (const line of lines) {
      if (line.quantity > 0 && (!line.zoneId || !line.conditionId)) {
        this.messageService.add({ severity: 'warn', summary: 'Placement required', detail: 'Choose a zone and condition for received stock.' });
        return;
      }
      const item = this.selectedItem(request.lines.find(row => row.id === line.lineId)?.itemId);
      if (this.itemRequiresUnitTracking(item) && line.quantity > 0 && !Number.isInteger(line.quantity)) {
        this.messageService.add({ severity: 'warn', summary: 'Whole units required', detail: 'Unit-tracked stock must be received in whole units.' });
        return;
      }
      if (this.itemRequiresUnitTracking(item) && line.unitCodes.length > line.quantity) {
        this.messageService.add({ severity: 'warn', summary: 'Too many unit codes', detail: 'Supplied codes cannot exceed received units.' });
        return;
      }
      const expected = request.lines.find(row => row.id === line.lineId)?.expected || 0;
      if ((line.quantity < expected || line.disposition === 'HOLD') && !line.exceptionReason) {
        this.messageService.add({ severity: 'warn', summary: 'Exception reason required', detail: 'Explain short or held cargo.' });
        return;
      }
    }
    this.isSaving = true;
    this.warehousingService.receiveShipmentInbound(request.id, lines, this.reviewedManualReceipts)
      .pipe(finalize(() => this.isSaving = false)).subscribe({
        next: response => {
          const receiptId = response.data?.lines.find(line => line.inboundReceiptId)?.inboundReceiptId;
          if (!receiptId) {
            this.messageService.add({ severity: 'success', summary: 'Count recorded', detail: 'The inbound shortage was recorded.' });
            this.router.navigate(['/warehousing/shipment-work/SHIPMENT', request.shipmentId]);
            return;
          }
          this.warehousingService.getInboundReceipt(receiptId).subscribe({
            next: receipt => {
              this.createdReceipt = receipt.data;
              if (this.createdReceipt?.id) this.uploadReceiptAttachments(this.createdReceipt);
              this.successDialogVisible = true;
            },
            error: () => this.router.navigate(['/warehousing/stock-entry/view', receiptId])
          });
        },
        error: error => this.messageService.add({ severity: 'error', summary: 'Receiving failed', detail: error?.error?.message || 'Could not receive inbound cargo.' })
      });
  }

  addLine(): void {
    this.linesArray.push(this.createLineGroup());
  }

  removeLine(index: number): void {
    if (this.linesArray.length > 1) {
      this.linesArray.removeAt(index);
    } else {
      this.linesArray.reset();
    }
  }

  duplicateLine(index: number): void {
    if (this.inboundRequest) return;
    const source = this.linesArray.at(index).value;
    const newLine = this.fb.group({
      itemId: [source.itemId, [Validators.required]],
      zoneId: [source.zoneId, [Validators.required]],
      conditionId: [source.conditionId, [Validators.required]],
      quantity: [source.quantity, [Validators.required, Validators.min(0.0001)]],
      transactionUomId: [source.transactionUomId],
      unitSkusText: [source.unitSkusText],
      packageQuantity: [source.packageQuantity],
      remarks: [source.remarks]
    });
    this.linesArray.insert(index + 1, newLine);
  }

  availableZones(): WarehousingMasterData[] {
    const warehouseId = this.bulkForm.getRawValue().warehouseId;
    if (!warehouseId) {
      return this.masterData.ZONE;
    }
    return this.masterData.ZONE.filter(zone => zone.parentId === warehouseId);
  }

  selectedItem(itemId?: number | null): WarehousingItem | undefined {
    if (!itemId) return undefined;
    return this.items.find(i => i.id === itemId);
  }

  inboundOwnerLabel(requestLineId?: number | null): string {
    const line = this.inboundRequest?.lines.find(row => row.id === requestLineId);
    if (!line) return '';
    if (!line.crmClientId) return 'Company owned';
    const client = this.clients.find(row => row.id === line.crmClientId)?.companyName || 'Client #' + line.crmClientId;
    const account = line.crmAccountId
      ? this.accounts.find(row => row.id === line.crmAccountId)?.companyName || 'Account #' + line.crmAccountId : '';
    return account ? client + ' · ' + account : client;
  }

  uomOptionsForItem(itemId?: number | null): Array<{ label: string; value: number }> {
    const item = this.selectedItem(itemId);
    if (!item) return [];
    const baseUomId = item.baseUomId || item.tenantUomId;
    const base = baseUomId ? [{ label: item.tenantUomName || 'Base UOM', value: baseUomId }] : [];
    return [...base, ...(item.uomConversions || []).filter(row => row.active !== false && row.uomId !== baseUomId).map(row => ({
      label: `${row.packLabel || 'Pack'} (${row.factorToBase} base units)`, value: row.uomId
    }))];
  }

  itemRequiresUnitTracking(item?: WarehousingItem): boolean {
    return !!item && (item.trackingMode === 'UNIT' || item.trackingMode === 'UNIQUE_ASSET' || !!item.isDisplayStand);
  }

  unitSkus(value?: string | null): string[] {
    return (value || '').split(/[\n,]+/).map(row => row.trim()).filter(Boolean);
  }

  baseQuantity(item: WarehousingItem | undefined, enteredQuantity: number | null | undefined,
      transactionUomId?: number | null): number {
    const conversion = (item?.uomConversions || [])
      .find(row => row.uomId === transactionUomId && row.active !== false);
    return Number(enteredQuantity || 0) * Number(conversion?.factorToBase || 1);
  }

  itemSize(item?: WarehousingItem): string {
    return item ? formatItemSize(item) : '-';
  }

  totalItemsCount(): number {
    return this.linesArray.length;
  }

  totalQuantitySum(): number {
    let sum = 0;
    for (let i = 0; i < this.linesArray.length; i++) {
      const qty = Number(this.linesArray.at(i).get('quantity')?.value || 0);
      sum += qty;
    }
    return sum;
  }

  driverOptions(): { id: number; displayLabel: string; legalName?: string; vehiclePlate?: string }[] {
    return this.drivers.map(driver => ({
      id: driver.id!,
      displayLabel: this.driverDisplayName(driver),
      legalName: driver.legalName,
      vehiclePlate: driver.currentVehicle?.plateNumber || ''
    }));
  }

  loadDrivers(search: string): void {
    const term = search.trim();
    if (term === this.driverSearch && this.driverPage >= 0 && !this.driverLoadError) return;
    this.driverSearch = term;
    this.driverPage = 0;
    this.driverHasMore = false;
    this.driverSearch$.next({ search: term, page: 0 });
  }

  loadMoreDrivers(event: { last?: number }): void {
    if (this.driversLoading || !this.driverHasMore || (event.last ?? 0) < this.drivers.length - 4) return;
    this.driverHasMore = false;
    this.driverSearch$.next({ search: this.driverSearch, page: this.driverPage + 1 });
  }

  driverDisplayName(driver?: Driver): string {
    if (!driver) return '-';
    const name = driver.name || driver.legalName || '';
    const phone = driver.phoneNumber ? ` (${driver.phoneNumber})` : '';
    const plate = driver.currentVehicle?.plateNumber ? ` [${driver.currentVehicle.plateNumber}]` : '';
    return (name || `Driver #${driver.id}`) + phone + plate;
  }

  selectedDriverPlate(): string {
    const driverId = this.bulkForm.getRawValue().driverId;
    const driver = this.drivers.find(d => d.id === driverId);
    return driver?.currentVehicle?.plateNumber || this.bulkForm.getRawValue().vehiclePlateNumber || '';
  }

  onDriverChange(driverId: number | null): void {
    if (driverId) {
      const driver = this.drivers.find(d => d.id === driverId);
      if (driver?.currentVehicle?.plateNumber) {
        this.bulkForm.patchValue({ vehiclePlateNumber: driver.currentVehicle.plateNumber });
      }
    }
  }

  openQuickFleetOnboarding(): void {
    this.quickFleetOnboardingVisible = true;
  }

  onQuickFleetOnboarded(result: QuickFleetOnboardingResult): void {
    if (result?.driver) {
      const existing = this.drivers.find(d => d.id === result.driver.id);
      if (!existing) {
        this.drivers = [result.driver, ...this.drivers];
      }
      this.bulkForm.patchValue({
        driverId: result.driver.id,
        vehiclePlateNumber: result.driver.currentVehicle?.plateNumber || ''
      });
      this.messageService.add({
        severity: 'success',
        summary: 'Fleet Onboarded',
        detail: `Driver ${this.driverDisplayName(result.driver)} was onboarded and assigned.`
      });
    }
  }

  createQuickSupplier(): void {
    if (this.quickSupplierForm.invalid || this.isCreatingSupplier) {
      this.quickSupplierForm.markAllAsTouched();
      return;
    }
    const companyId = this.sessionStorageService.getCompany()?.id || 0;
    if (!companyId) {
      this.messageService.add({ severity: 'error', summary: 'Supplier', detail: 'Select an operating company before creating a supplier.' });
      return;
    }
    const value = this.quickSupplierForm.getRawValue();
    const companyName = value.companyName!.trim();
    this.isCreatingSupplier = true;
    this.crmService.addClient({
      companyName,
      customerNameLocal: companyName,
      notifyingPersonName: companyName,
      postingDate: new Date(),
      currencyCode: 'SAR',
      exchangeRate: 1,
      creditCheckMode: 'DISABLED',
      creditHold: false,
      overdueToleranceDays: 0,
      businessType: 'B2B',
      roles: ['SUPPLIER'],
      primaryEmail: value.primaryEmail?.trim() || '',
      phoneNumber: value.phoneNumber?.trim() || '',
      category: 'STANDARD',
      website: '',
      source: 'WAREHOUSE_RECEIPT',
      kamCustomerId: 0,
      companyId
    }).pipe(finalize(() => this.isCreatingSupplier = false)).subscribe({
      next: response => {
        if (!response.success || !response.data?.id) return;
        this.clients = [response.data, ...this.clients.filter(client => client.id !== response.data?.id)];
        this.bulkForm.patchValue({ supplierId: response.data.id });
        this.quickSupplierVisible = false;
        this.quickSupplierForm.reset();
        this.messageService.add({ severity: 'success', summary: 'Supplier created', detail: `${response.data.companyName} is selected for this receipt.` });
      },
      error: error => this.messageService.add({ severity: 'error', summary: 'Supplier not created', detail: error?.error?.message || 'The supplier could not be created.' })
    });
  }

  submitBulkReceiving(): void {
    if (this.isSaving) return;
    if (this.inboundRequest) { this.submitLinkedInbound(); return; }

    if (this.bulkForm.invalid) {
      this.bulkForm.markAllAsTouched();
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation Required',
        detail: 'Please complete all required fields for the receipt header and lines.'
      });
      return;
    }

    if (this.linesArray.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No Lines',
        detail: 'Add at least one item line to receive.'
      });
      return;
    }

    for (const line of this.linesArray.getRawValue()) {
      const item = this.selectedItem(line.itemId);
      const unitSkus = this.unitSkus(line.unitSkusText);
      if (this.itemRequiresUnitTracking(item)
          && unitSkus.length > this.baseQuantity(item, line.quantity, line.transactionUomId)) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Too many unit codes',
          detail: 'Supplied unit codes cannot exceed the received base quantity.'
        });
        return;
      }
    }

    const raw = this.bulkForm.getRawValue();
    const warehouseId = raw.warehouseId!;
    const warehouse = this.masterData.WAREHOUSE.find(w => w.id === warehouseId);
    const regionId = warehouse?.parentId || 0;

    const commandLines: WarehousingStockEntry[] = raw.lines.map((line: any) => {
      const item = this.items.find(i => i.id === line.itemId);
      return {
        itemId: line.itemId,
        regionId: regionId,
        warehouseId: warehouseId,
        zoneId: line.zoneId,
        conditionId: line.conditionId,
        quantity: line.quantity,
        transactionQuantity: line.transactionUomId ? line.quantity : undefined,
        transactionUomId: line.transactionUomId || undefined,
        unitSkus: this.itemRequiresUnitTracking(item) ? this.unitSkus(line.unitSkusText) : undefined,
        packageQuantity: line.packageQuantity || undefined,
        entryDate: raw.receiptDate,
        notes: line.remarks || undefined,
        referenceNumber: raw.referenceNumber || undefined,
        crmClientId: item?.crmClientId || undefined,
        crmAccountId: item?.crmAccountId || undefined,
        stockOwnerType: item?.crmClientId ? 'CRM_CLIENT_OWNED' : 'TENANT_OWNED'
      } as WarehousingStockEntry;
    });

    const command: WarehousingBulkReceiveCommand = {
      referenceNumber: raw.referenceNumber || undefined,
      supplierId: raw.supplierId || undefined,
      driverId: raw.driverId || undefined,
      vehiclePlateNumber: this.selectedDriverPlate() || undefined,
      warehouseId: warehouseId,
      receiptDate: raw.receiptDate || new Date(),
      notes: raw.notes || undefined,
      lines: commandLines
    };

    this.isSaving = true;
    this.warehousingService.bulkReceiveStock(command).pipe(
      finalize(() => this.isSaving = false)
    ).subscribe({
      next: (res) => {
        this.createdReceipt = res.data;
        if (res.data?.id) this.uploadReceiptAttachments(res.data);
        this.successDialogVisible = true;
        this.messageService.add({
          severity: 'success',
          summary: 'Bulk Receipt Created',
          detail: `Receipt ${res.data?.receiptNumber || ''} created successfully with ${commandLines.length} items.`
        });
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Receiving Failed',
          detail: err?.error?.message || 'Could not complete bulk receiving.'
        });
      }
    });
  }

  onDnFiles(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.dnFiles = [...this.dnFiles, ...Array.from(input.files || [])];
    input.value = '';
  }

  removeDnFile(index: number): void {
    this.dnFiles.splice(index, 1);
    this.dnFiles = [...this.dnFiles];
  }

  private uploadReceiptAttachments(receipt: WarehousingInboundReceipt): void {
    for (const file of this.dnFiles) {
      this.firebaseStorageService.uploadFileAndGetPublicUrl(file, `warehousing/inbound-receipts/${receipt.id}/dn`)
        .subscribe({
          next: fileUrl => this.warehousingService.addInboundReceiptAttachment(receipt.id!, {
            fileName: file.name,
            fileUrl,
            contentType: file.type || undefined,
            fileSize: file.size
          }).subscribe({
            next: response => {
              if (response.data) this.createdReceipt = response.data;
            },
            error: () => this.messageService.add({ severity: 'warn', summary: 'DN attachment', detail: `${file.name} uploaded but could not be linked to the GRN.` })
          }),
          error: () => this.messageService.add({ severity: 'warn', summary: 'DN attachment', detail: `${file.name} could not be uploaded.` })
        });
    }
  }

  downloadCreatedGrn(): void {
    if (!this.createdReceipt?.id) return;
    this.isLoading = true;
    this.warehousingService.downloadGrnReport(this.createdReceipt.id).subscribe({
      next: (blob) => {
        this.isLoading = false;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `grn-${this.createdReceipt?.receiptNumber || this.createdReceipt?.id}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      },
      error: () => {
        this.isLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Download Error',
          detail: 'Could not generate Goods Receipt Note (GRN) PDF.'
        });
      }
    });
  }

  goToReceipts(): void {
    this.successDialogVisible = false;
    this.router.navigate(['/warehousing/stock-entry']);
  }

  resetForNewReceipt(): void {
    this.successDialogVisible = false;
    if (this.inboundRequest) {
      this.router.navigate(['/warehousing/shipment-work/SHIPMENT', this.inboundRequest.shipmentId],
        { queryParams: { direction: 'INBOUND' } });
      return;
    }
    this.createdReceipt = null;
    this.dnFiles = [];
    this.bulkForm.reset({
      referenceNumber: '',
      supplierId: null,
      driverId: null,
      vehiclePlateNumber: '',
      warehouseId: this.masterData.WAREHOUSE[0]?.id || null,
      receiptDate: new Date(),
      notes: ''
    });
    this.linesArray.clear();
    this.addLine();
  }
}
